import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonIdentifierTypes.Actions';
import { PersonIdentifierTypeState, PersonIdentifierTypeUnloadState } from '../states/PersonIdentifierTypeState';
import { IdentifierTypeViewModel } from '../../models/Entities/Person/IdentifierType/IdentifierTypeModels';

export type KnownAction =
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetAllRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetAllSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetAllFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetByIdRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetByIdSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetByIdFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeCreateRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeCreateSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeCreateFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeUpdateRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeUpdateSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeUpdateFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeDeleteRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeDeleteSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeDeleteFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeStatusRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeStatusSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeStatusFailureAction;

export const PersonIdentifierTypesReducer: Reducer<PersonIdentifierTypeState> = (
    state: PersonIdentifierTypeState | undefined,
    incomingAction: Action
): PersonIdentifierTypeState => {
    if (state === undefined) {
        return PersonIdentifierTypeUnloadState as PersonIdentifierTypeState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_SUCCESS: {
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: action.payload,
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: IdentifierTypeViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_SUCCESS: {
            const createdList = state.list.some((item: IdentifierTypeViewModel) => item.id === action.payload.id)
                ? state.list.map((item: IdentifierTypeViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: IdentifierTypeViewModel) => item.id === action.payload.id)
                ? state.list.map((item: IdentifierTypeViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: IdentifierTypeViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        // Activar / desactivar (PATCH .../status): usa los indicadores de la modificación y reemplaza el tipo
        // con el que devuelve el servidor.
        case ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_SUCCESS:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: state.list.some((item: IdentifierTypeViewModel) => item.id === action.payload.id)
                    ? state.list.map((item: IdentifierTypeViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                    : [...state.list, action.payload],
                error: undefined
            };
        case ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };
        default:
            return state;
    }
};
